import React from 'react';
import { View, StyleSheet, Pressable, Image, Dimensions, ImageBackground } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Text, Theme } from '../../../components/ui';
import { FireInsight, FireBook } from '../../../models/FirestoreModels';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');
const TAB_BAR_HEIGHT = 70; // Height of bottom tab bar
const AVAILABLE_HEIGHT = SCREEN_HEIGHT - TAB_BAR_HEIGHT;

interface InsightCardProps {
  insight: FireInsight;
  book?: FireBook;
  onRead: () => void;
  onListen: () => void;
  backgroundImage?: string;
}

/**
 * Individual insight card for the feed
 * Full-screen card with image, title, snippet, and actions
 */
const InsightCard: React.FC<InsightCardProps> = ({
  insight,
  book,
  onRead,
  onListen,
  backgroundImage,
}) => {
  // Calculate reading time (rough estimate: 200 words per minute)
  const getReadTime = () => {
    if (!insight.storyContent) return '2 min';
    const words = insight.storyContent.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min`;
  };

  // Get listen time from audio segment or estimate
  const getListenTime = () => {
    // TODO: Calculate from audio segment when available
    return '3 min';
  };

  // Get source text - short version (before colon)
  const getSource = () => {
    if (book) {
      const shortTitle = book.title.split(':')[0].trim();
      return `${shortTitle} by ${book.author}`;
    }
    return 'Mentorist Library';
  };

  // Get insight summary (whatToAchieve - the short description)
  const getInsightSnippet = () => {
    // Use whatToAchieve field which is the concise summary
    if (insight.whatToAchieve) {
      return insight.whatToAchieve;
    }
    // Fallback to storyContent if whatToAchieve is not available
    if (!insight.storyContent) return '';
    const text = insight.storyContent.replace(/<[^>]*>/g, '');
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
    const snippet = sentences.slice(0, 2).join(' ');
    return snippet.length > 200 ? snippet.substring(0, 200) + '...' : snippet;
  };

  return (
    <View style={styles.container}>
      {/* Background Image with Gradient Overlay */}
      <View style={styles.backgroundContainer}>
        {backgroundImage ? (
          <Image
            source={{ uri: backgroundImage }}
            style={styles.backgroundImage}
            resizeMode="cover"
          />
        ) : (
          <View style={[styles.backgroundImage, styles.defaultBackground]} />
        )}
        {/* Gradient Overlay - stronger at bottom */}
        <LinearGradient
          colors={['rgba(0, 0, 0, 0.3)', 'rgba(0, 0, 0, 0.5)', 'rgba(0, 0, 0, 0.9)']}
          locations={[0, 0.5, 1]}
          style={styles.gradientOverlay}
        />
      </View>

      {/* Content - Centered */}
      <View style={styles.contentWrapper}>
        {/* Content Card with distinct background */}
        <View style={styles.contentCard}>
          {/* Source Info */}
          <View style={styles.sourceContainer}>
            <Text style={styles.sourceDot}>•</Text>
            <Text style={styles.sourceText} numberOfLines={1}>
              {getSource()}
            </Text>
          </View>

          {/* Insight Title */}
          <Text style={styles.insightTitle} numberOfLines={3}>
            {insight.title}
          </Text>

          {/* Insight Description */}
          <Text style={styles.insightDescription} numberOfLines={4}>
            {getInsightSnippet()}
          </Text>

          {/* Action Buttons */}
          <View style={styles.actionsContainer}>
            <Pressable
              style={[styles.actionButton, styles.listenButton]}
              onPress={onListen}
            >
              <Ionicons name="headset-outline" size={16} color="#fff" />
              <Text style={styles.actionButtonText}>Listen</Text>
            </Pressable>

            <Pressable
              style={[styles.actionButton, styles.readButton]}
              onPress={onRead}
            >
              <Ionicons name="book-outline" size={16} color="#fff" />
              <Text style={styles.actionButtonText}>Read</Text>
            </Pressable>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: SCREEN_HEIGHT,
    width: '100%',
    backgroundColor: '#000',
    position: 'relative',
  },
  backgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  backgroundImage: {
    width: '100%',
    height: '100%',
  },
  defaultBackground: {
    backgroundColor: '#1a2a3a',
  },
  gradientOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  contentWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: TAB_BAR_HEIGHT + 24,
  },
  contentCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 24,
    elevation: 10,
  },
  sourceContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sourceDot: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.7)',
    marginRight: 6,
  },
  sourceText: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: 'rgba(255, 255, 255, 0.7)',
    flex: 1,
  },
  insightTitle: {
    fontSize: 24,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: '#fff',
    lineHeight: 32,
    marginBottom: 16,
  },
  insightDescription: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 21,
    marginBottom: 24,
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    borderRadius: 12,
  },
  listenButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  readButton: {
    backgroundColor: '#10b981', // Emerald-500
    borderWidth: 0,
  },
  actionButtonText: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: '#fff',
  },
});

export default React.memo(InsightCard);

